/**
 * SuggestedHoursConfirm — approve AI-suggested hour entries under the Oppdatering composer.
 * Same pattern as SuggestedTodosConfirm: checkbox list/table, never auto-creates.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  CheckCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolidIcon } from "@heroicons/react/24/solid";
import { createHour } from "../../../api/hour";
import {
  fetchProjectActivities,
  fetchProjectActivityGroups,
} from "../../../api/project_activity";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { GroupedActivityOptions } from "../../utils/moduleSelectors/projectActivityGroupUtils";
import { TWclassNames } from "../../utils/Div";

function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function hourSuggestionKey(hour) {
  if (!hour) return "";
  return [
    hour.project_id || hour.project_name || "",
    hour.project_activity_id || hour.activity_name || "",
    hour.date || "",
    hour.time ?? "",
    String(hour.description || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " "),
  ].join("|");
}

function hydrateHour(hour, defaults) {
  const time = Number(hour?.time);
  return {
    project_id: hour?.project_id || defaults.projectId || null,
    project_name: hour?.project_name || defaults.projectName || "",
    project_activity_id: hour?.project_activity_id || defaults.activityId || null,
    activity_name: hour?.activity_name || defaults.activityName || "",
    description: hour?.description || "",
    time: Number.isFinite(time) && time > 0 ? time : "",
    date: hour?.date || defaults.date || todayISO(),
  };
}

function hourCreatedId(res) {
  if (!res || res.__error) return null;
  if (res.id) return res.id;
  if (res.data?.id) return res.data.id;
  return null;
}

export function SuggestedHoursConfirm({
  hours = [],
  createdHours = [],
  defaultProjectId = null,
  defaultProjectName = "",
  defaultActivityId = null,
  defaultActivityName = "",
  onCreated,
  onDismiss,
}) {
  const { t } = useTranslation();
  const defaults = useMemo(
    () => ({
      projectId: defaultProjectId,
      projectName: defaultProjectName,
      activityId: defaultActivityId,
      activityName: defaultActivityName,
      date: todayISO(),
    }),
    [defaultProjectId, defaultProjectName, defaultActivityId, defaultActivityName]
  );

  const pending = Array.isArray(hours) ? hours : [];
  const created = Array.isArray(createdHours) ? createdHours : [];
  const hasPending = pending.length > 0;
  const hasCreated = created.length > 0;

  const [drafts, setDrafts] = useState({});
  const [selected, setSelected] = useState({});
  const [creating, setCreating] = useState(false);
  const [activitiesByProject, setActivitiesByProject] = useState({});
  const [groupsByProject, setGroupsByProject] = useState({});

  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      pending.forEach((hour, index) => {
        const key = hourSuggestionKey(hour) || `row-${index}`;
        if (!next[key]) next[key] = hydrateHour(hour, defaults);
      });
      return next;
    });
    setSelected((prev) => {
      const next = {};
      pending.forEach((hour, index) => {
        const key = hourSuggestionKey(hour) || `row-${index}`;
        next[key] = Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : true;
      });
      return next;
    });
  }, [hours, defaults]);

  const projectIds = useMemo(() => {
    const ids = new Set();
    Object.values(drafts).forEach((row) => {
      if (row?.project_id) ids.add(String(row.project_id));
    });
    if (defaultProjectId) ids.add(String(defaultProjectId));
    return Array.from(ids);
  }, [drafts, defaultProjectId]);

  useEffect(() => {
    let cancelled = false;
    projectIds.forEach((pid) => {
      if (activitiesByProject[pid]) return;
      Promise.all([
        fetchProjectActivities(pid),
        fetchProjectActivityGroups(pid).catch(() => []),
      ])
        .then(([rows, groups]) => {
          if (cancelled) return;
          setActivitiesByProject((prev) => ({
            ...prev,
            [pid]: Array.isArray(rows) ? rows : [],
          }));
          setGroupsByProject((prev) => ({
            ...prev,
            [pid]: Array.isArray(groups) ? groups : [],
          }));
        })
        .catch(() => {
          if (cancelled) return;
          setActivitiesByProject((prev) => ({ ...prev, [pid]: [] }));
          setGroupsByProject((prev) => ({ ...prev, [pid]: [] }));
        });
    });
    return () => {
      cancelled = true;
    };
  }, [projectIds, activitiesByProject]);

  if (!hasPending && !hasCreated) return null;

  const updateDraft = (key, patch) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const toggle = (key) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const pendingRows = pending.map((hour, index) => {
    const key = hourSuggestionKey(hour) || `row-${index}`;
    return { key, hour, draft: drafts[key] || hydrateHour(hour, defaults) };
  });

  const confirmed = pendingRows.filter(({ key, draft }) => {
    if (!selected[key]) return false;
    const time = Number(draft?.time);
    return (
      draft?.project_id &&
      draft?.project_activity_id &&
      draft?.date &&
      Number.isFinite(time) &&
      time > 0
    );
  });
  const canCreate = confirmed.length > 0 && !creating;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    let ok = 0;
    let fail = 0;
    const createdRows = [];

    for (const { draft } of confirmed) {
      const time = Number(draft.time);
      try {
        const res = await createHour({
          project_id: draft.project_id,
          project_activity_id: draft.project_activity_id,
          date: draft.date,
          time,
          description: String(draft.description || "").trim() || undefined,
        });
        const id = hourCreatedId(res);
        if (id) {
          ok += 1;
          createdRows.push({
            ...draft,
            time,
            hourId: id,
            status: "created",
          });
        } else {
          fail += 1;
        }
      } catch {
        fail += 1;
      }
    }

    setCreating(false);
    if (ok > 0) {
      toast.success(
        t("oppdatering.hours_created", {
          count: ok,
          defaultValue: `${ok} timeføring(er) opprettet`,
        })
      );
      onCreated?.(createdRows);
    }
    if (fail > 0) {
      toast.error(
        t("oppdatering.hours_create_failed", {
          count: fail,
          defaultValue: `Kunne ikke registrere ${fail} timeføring(er)`,
        })
      );
    }
  };

  const renderRowFields = (key, draft) => {
    const activities = draft?.project_id
      ? activitiesByProject[String(draft.project_id)] || []
      : [];
    const groups = draft?.project_id
      ? groupsByProject[String(draft.project_id)] || []
      : [];
    return (
      <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <div className="min-w-0">
          <p className="mb-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {t("oppdatering.hours_col_project")}
          </p>
          <ProjectSelector
            noLabel
            selected={
              draft?.project_id
                ? {
                    id: draft.project_id,
                    displayTitle: draft.project_name || `#${draft.project_id}`,
                  }
                : null
            }
            onParentObjectChange={(obj) => {
              updateDraft(key, {
                project_id: obj?.id || null,
                project_name: obj?.displayTitle || obj?.name || "",
                project_activity_id: null,
                activity_name: "",
              });
            }}
          />
        </div>
        <div className="min-w-0">
          <label className="mb-0.5 block text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {t("oppdatering.hours_col_activity")}
          </label>
          <select
            value={draft?.project_activity_id || ""}
            disabled={!draft?.project_id}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : null;
              const row = activities.find((a) => Number(a.id) === Number(id));
              updateDraft(key, {
                project_activity_id: id,
                activity_name:
                  row?.display_title || row?.displayTitle || row?.title || "",
              });
            }}
            className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 disabled:bg-slate-50"
          >
            <GroupedActivityOptions
              activities={activities}
              groups={groups}
              ungroupedLabel={t("project_activity.ungrouped")}
              emptyLabel={
                draft?.project_id
                  ? t("oppdatering.hours_missing_activity")
                  : t("oppdatering.hours_missing_project")
              }
            />
          </select>
        </div>
        <div className="min-w-0 sm:col-span-2 lg:col-span-1">
          <label className="mb-0.5 block text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {t("oppdatering.hours_col_description")}
          </label>
          <input
            type="text"
            value={draft?.description || ""}
            onChange={(e) => updateDraft(key, { description: e.target.value })}
            className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-800"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 lg:col-span-1">
          <div>
            <label className="mb-0.5 block text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t("oppdatering.hours_col_time")}
            </label>
            <input
              type="number"
              min="0.25"
              max="24"
              step="0.25"
              value={draft?.time ?? ""}
              onChange={(e) => updateDraft(key, { time: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-800"
            />
          </div>
          <div>
            <label className="mb-0.5 block text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t("oppdatering.hours_col_date")}
            </label>
            <input
              type="date"
              value={draft?.date || ""}
              onChange={(e) => updateDraft(key, { date: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-800"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
      <div className="mb-2 flex items-center gap-2">
        <ClockIcon className="h-5 w-5 text-[#1463C7]" />
        <h4 className="text-sm font-semibold text-slate-900">
          {t("oppdatering.hours_title")}
        </h4>
        {hasPending && (
          <span className="text-xs text-slate-500">{t("oppdatering.hours_hint")}</span>
        )}
      </div>

      {hasPending && (
        <ul className="flex flex-col gap-2">
          {pendingRows.map(({ key, draft }) => {
            const checked = !!selected[key];
            return (
              <li key={key}>
                <div
                  className={TWclassNames(
                    "flex items-start gap-2.5 rounded-xl border bg-white px-3 py-2.5",
                    checked
                      ? "border-[#BFDCF7] bg-[#EAF3FD]/40"
                      : "border-slate-200"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(key)}
                    className="mt-2 h-4 w-4 flex-shrink-0 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
                  />
                  {renderRowFields(key, draft)}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {hasCreated && (
        <div className={hasPending ? "mt-3 border-t border-slate-200 pt-3" : ""}>
          {hasPending && (
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("oppdatering.hours_created_section")}
            </p>
          )}
          <ul className="flex flex-col gap-1.5">
            {created.map((row, i) => {
              const label = [
                row.project_name,
                row.activity_name,
                row.time != null ? `${row.time} t` : null,
                row.description,
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <li key={`created-hour:${i}:${row.hourId || label}`}>
                  <div className="flex items-start gap-2.5 rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2.5">
                    <CheckCircleSolidIcon
                      className="mt-0.5 h-4 w-4 shrink-0 text-[#0B7A3B]"
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      {row.hourId ? (
                        <Link
                          to="/dashboard/hours"
                          className="text-sm font-medium text-slate-800 underline-offset-2 hover:underline"
                        >
                          {label || t("oppdatering.hours_open")}
                        </Link>
                      ) : (
                        <span className="text-sm text-slate-800">{label}</span>
                      )}
                    </div>
                    <span className="inline-flex shrink-0 items-center rounded-full bg-[#BBF7D0] px-2 py-0.5 text-xs font-semibold text-[#0B7A3B]">
                      {t("oppdatering.hours_created_badge")}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-xl px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            {hasPending ? t("Hopp over") : t("Lukk", { defaultValue: "Lukk" })}
          </button>
        )}
        {hasPending && (
          <button
            type="button"
            onClick={handleCreate}
            disabled={!canCreate}
            className={TWclassNames(
              "inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-all",
              canCreate
                ? "bg-[linear-gradient(135deg,#1463C7,#2D8BE8)] text-white shadow-[0_12px_24px_-8px_rgba(20,99,199,.6)]"
                : "cursor-not-allowed bg-slate-100 text-slate-400"
            )}
          >
            <CheckCircleIcon className="h-5 w-5" />
            {creating
              ? t("Oppretter…")
              : t("Opprett valgte", { count: confirmed.length })}
          </button>
        )}
      </div>
    </div>
  );
}

export default SuggestedHoursConfirm;
